import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface User {
  user_id?: string;
  email?: string;
  name?: string;
  role: 'admin' | 'employee' | 'superadmin';
  [key: string]: any; // Allow additional properties
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  userLogin: (userData: User) => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Check authentication on initial load
  useEffect(() => {
    const loadUserFromStorage = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          
          // Ensure the user has a role property
          if (parsedUser && (parsedUser.role || parsedUser.isAdmin !== undefined || parsedUser.isSuperAdmin !== undefined)) {
            // Handle legacy data or new data
            let userRole = 'employee';
            if (parsedUser.role) {
              userRole = parsedUser.role;
            } else if (parsedUser.isSuperAdmin) {
              userRole = 'superadmin';
            } else if (parsedUser.isAdmin) {
              userRole = 'admin';
            }
            
            const userData = {
              ...parsedUser,
              role: userRole
            };
            
            setIsAuthenticated(true);
            setUser(userData);
          } else {
            localStorage.removeItem("user");
          }
        }
      } catch (error) {
        console.error("Error loading user from localStorage:", error);
        localStorage.removeItem("user");
      } finally {
        setIsLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);

  const userLogin = (userData: User) => {
    localStorage.setItem("user", JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
    // router("/homepage");
  };

  const logout = () => {
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    setUser(null);
    // router("/login");
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, userLogin, logout, hasRole, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
